// Favorito.tsx

import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../security/AuthLogin";
import { fetchFavoritoById, updateEstadoFavorito } from "../api/favoritosApi";
import {
  fetchSoporteMensajes,
  sendSoporteMensaje,
  fetchArchivosSoporte,
} from "../api/soportesApi";
import { getAuthToken } from "../security/AuthLogin";
import { io, Socket } from "socket.io-client";
import DOMPurify from "dompurify";
import "react-quill/dist/quill.snow.css";
import "../css/favorito.css";
import ChatComponent from "../components/ChatComponent";

interface FavoritoData {
  id_concurso: string;
  id_usuario: string; // ID del usuario al que pertenece el favorito (no el conectado)
  titulo: string;
  codigo_expediente: string;
  enlace: string | null;
  estado_concurso: string;
  estado_soporte: string | null;
  id_soporte: number;
  cliente_nombre?: string;
  cliente_email?: string;
  cliente_profilepic?: string;
  dueno_id?: string | null;
  dueno_nombre?: string | null;
  dueno_email?: string | null;
  // NUEVOS CAMPOS (información adicional del cliente)
  fecha_constitucion?: string;
  habilitacion_profesional?: string;
  clasificaciones_reconocidas?: string;
  cifra_anual_negocio?: string;
  certificados_cbe?: string;
  plan_igualdad?: string;
  numero_empleados?: string;
  otros_contactos?: string;
  presentado?: number;
  observaciones?: string;
}

interface MensajeSoporte {
  id_soporte_detalle: number;
  id_soporte: number;
  id_usuario: string;
  f_respuesta: string;
  hora_respuesta: string;
  texto: string;
  es_cliente: boolean;
}

interface ArchivoSoporte {
  path: string;
  uploaderRole: "cliente" | "admin" | "tecnico";
  dateFolder?: string | null; // la subcarpeta de fecha (YYYY-MM-DD)
}

interface TecnicoData {
  id_usuario: string;
  nombre: string;
  email: string;
}

const BASE_URL = `${window.location.protocol}//${window.location.hostname}`;

// Mapeo del estado (capitalizando la primera letra)
const mapEstado = (estado: string | null): string => {
  if (!estado) return "En estudio";
  switch (estado.toLowerCase()) {
    case "en estudio":
      return "En estudio";
    case "valido":
      return "Viable";
    case "rechazado":
      return "No viable";
    case "oculto":
      return "Oculto";
    case "norecomendado":
      return "No recomendado";
    default:
      return estado.charAt(0).toUpperCase() + estado.slice(1);
  }
};

const Favorito: React.FC = () => {
  // Parámetros de URL
  const { id_concurso, id_usuario } = useParams<{
    id_concurso: string;
    id_usuario: string;
  }>();
  const { userRole } = useAuth();

  const [, setLoading] = useState(false);
  const [favorito, setFavorito] = useState<FavoritoData | null>(null);
  const [, setMensajes] = useState<MensajeSoporte[]>([]);
  const [, setArchivos] = useState<ArchivoSoporte[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [, setAlguienTyping] = useState({
    isTyping: false,
    quien: "",
  });
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [tecnicosActivos, setTecnicosActivos] = useState<TecnicoData[]>([]);
  const [selectedTecnico, setSelectedTecnico] = useState("");
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [observaciones, setObservaciones] = useState("");
  const [observacionesOriginales, setObservacionesOriginales] = useState("");

  // ★ NUEVO: control del modal de confirmación
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmType, setConfirmType] = useState<
    "baja" | "tecnico" | "administrativo" | "juridico" | null
  >(null);
  const [confirmMessage, setConfirmMessage] = useState("");

  // ★ NUEVO: mapeos de tipos a rutas y textos
  const tipoConfig = {
    baja: {
      label: "Baja estadística",
      endpoint: "/api/baja-estadistica",
      redirectPath: (c: string, u: string) => `/baja-estadistica/${c}/${u}`,
    },
    tecnico: {
      label: "Soporte técnico",
      endpoint: "/api/soporte-tecnico",
      redirectPath: (c: string, u: string) => `/soporte-tecnico/${c}/${u}`,
    },
    administrativo: {
      label: "Soporte administrativo",
      endpoint: "/api/soporte-administrativo",
      redirectPath: (c: string, u: string) =>
        `/soporte-administrativo/${c}/${u}`,
    },
    juridico: {
      label: "Soporte jurídico",
      endpoint: "/api/soporte-juridico",
      redirectPath: (c: string, u: string) => `/soporte-juridico/${c}/${u}`,
    },
  };

  // ★ NUEVO: comprobar existencia de soporte
  const checkSupportExists = async (type: keyof typeof tipoConfig) => {
    if (!favorito) return false;
    const token = getAuthToken();
    const resp = await fetch(
      `/api/soporte-existe?tipo=${type}&id_concurso=${favorito.id_concurso}&id_usuario=${favorito.id_usuario}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!resp.ok) return false;
    const json = await resp.json();
    return json.exists as boolean;
  };

  // ★ NUEVO: wrapper para todos los botones excepto “Ver concurso”
  const handleButtonClick = async (type: keyof typeof tipoConfig) => {
    if (!favorito) return;
    const exists = await checkSupportExists(type);
    if (exists) {
      // ya existe → llamar directo al handler sin confirmación
      await invokeSupportHandler(type, "");
    } else {
      // no existe → abre modal
      setConfirmType(type);
      setConfirmMessage("");
      setConfirmModalVisible(true);
    }
  };

  // ★ NUEVO: función que invoca el endpoint, envía mensaje opcional y redirige
  const invokeSupportHandler = async (
    type: keyof typeof tipoConfig,
    initialMessage: string
  ) => {
    if (!favorito) return;
    const { endpoint, redirectPath } = tipoConfig[type];
    try {
      const token = getAuthToken();
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id_concurso: favorito.id_concurso,
          id_usuario: favorito.id_usuario,
        }),
      });
      if (!response.ok)
        throw new Error((await response.text()) || "Error al crear soporte");
      const data = await response.json();
      if (data.success && data.id_soporte) {
        if (initialMessage.trim()) {
          // enviar mensaje inicial
          await sendSoporteMensaje(
            data.id_soporte,
            DOMPurify.sanitize(initialMessage)
          );
        }
        // redirigir
        window.location.href = redirectPath(
          favorito.id_concurso,
          favorito.id_usuario
        );
      } else {
        alert("No se pudo obtener el ID del soporte");
      }
    } catch (err: any) {
      console.error(err);
      alert(err.message || "Error procesando la solicitud");
    }
  };

  // ★ NUEVO: al confirmar en el modal, disparamos la llamada
  const handleConfirmYes = async () => {
    if (confirmType) {
      await invokeSupportHandler(confirmType, confirmMessage);
      setConfirmModalVisible(false);
    }
  };
  const handleConfirmNo = () => {
    setConfirmModalVisible(false);
  };

  // Conexión al socket
  useEffect(() => {
    const s = io(BASE_URL, {
      withCredentials: true,
      auth: (cb) => cb({ token: localStorage.getItem("authToken") }),
    });
    setSocket(s);
    return () => {
      s.disconnect();
    };
  }, []);

  // Cargar favorito
  useEffect(() => {
    if (id_concurso && id_usuario) {
      loadFavorito(id_concurso, id_usuario);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id_concurso, id_usuario]);

  const loadFavorito = async (concursoId: string, favUserId: string) => {
    setLoading(true);
    try {
      const fav = await fetchFavoritoById(concursoId, favUserId);
      setFavorito(fav);
      if (fav.observaciones) {
        setObservaciones(fav.observaciones);
        setObservacionesOriginales(fav.observaciones);
      }
      if (fav.id_soporte) {
        await loadChat(fav.id_soporte);
        await loadArchivos(fav.id_soporte);
      }
    } catch (error: any) {
      alert(`Error al cargar favorito: ${error.message}`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadChat = async (idSoporte: number) => {
    try {
      const resp = await fetchSoporteMensajes(idSoporte);
      if (resp.success) {
        setMensajes(resp.messages);
        scrollToBottom();
      }
    } catch (error: any) {
      alert(`Error al cargar mensajes: ${error.message}`);
      console.error(error);
    }
  };

  const loadArchivos = async (idSoporte: number) => {
    try {
      const resp = await fetchArchivosSoporte(idSoporte);
      if (resp.success) {
        setArchivos(resp.archivos);
      }
    } catch (error: any) {
      alert(`Error al cargar archivos: ${error.message}`);
      console.error(error);
    }
  };

  useEffect(() => {
    if (socket && favorito?.id_soporte) {
      socket.emit("joinSoporte", favorito.id_soporte);
      socket.on("nuevoMensaje", (mensaje: MensajeSoporte) => {
        setMensajes((prev) => [...prev, mensaje]);
        scrollToBottom();
      });
      socket.on("alguienEstaEscribiendo", ({ isTyping, quien }) => {
        setAlguienTyping({ isTyping, quien });
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        if (isTyping) {
          typingTimeoutRef.current = setTimeout(() => {
            setAlguienTyping({ isTyping: false, quien: "" });
          }, 5000);
        } else {
          setAlguienTyping({ isTyping: false, quien: "" });
        }
      });
    }
    return () => {
      if (socket) {
        socket.off("nuevoMensaje");
        socket.off("alguienEstaEscribiendo");
      }
    };
  }, [socket, favorito?.id_soporte]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (userRole === "admin") {
      fetchTecnicos();
    }
  }, [userRole]);

  const fetchTecnicos = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const resp = await fetch(`${BASE_URL}/api/usuarios/tecnicos-activos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Error al obtener técnicos: ${txt}`);
      }
      const json = await resp.json();
      if (json.success) {
        setTecnicosActivos(json.tecnicos);
      }
    } catch (error) {
      console.error("Error fetching technicians:", error);
    }
  };

  const handleEstadoChange = async (newEstado: string) => {
    if (!favorito) return;
    try {
      await updateEstadoFavorito(
        favorito.id_concurso,
        favorito.id_usuario,
        newEstado
      );
      setFavorito((prev) =>
        prev
          ? {
              ...prev,
              estado_soporte: newEstado === "En estudio" ? null : newEstado,
            }
          : prev
      );
    } catch (error: any) {
      alert(`Error al actualizar estado: ${error.message}`);
      console.error(error);
    }
  };

  const handlePresentadoChange = async (isChecked: boolean) => {
    if (!favorito) return;
    try {
      const token = getAuthToken();
      if (!token) throw new Error("No autenticado");
      const response = await fetch(
        `${BASE_URL}/api/favoritos/presentado/${favorito.id_concurso}/${favorito.id_usuario}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ presentado: isChecked ? 1 : 0 }),
        }
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Error al actualizar presentado");
      }
      const data = await response.json();
      if (data.success) {
        // Actualizamos el estado local con el nuevo valor
        setFavorito((prev) =>
          prev ? { ...prev, presentado: isChecked ? 1 : 0 } : prev
        );
      } else {
        alert(data.message || "Error al actualizar presentado");
      }
    } catch (error: any) {
      console.error("Error updating presentado:", error);
      alert("Error al actualizar presentado: " + error.message);
    }
  };

  // ★ NUEVA FUNCIÓN: Guardar observaciones
  const saveObservaciones = async () => {
    if (!favorito) return;
    try {
      const token = getAuthToken();
      if (!token) throw new Error("No autenticado");

      const response = await fetch(
        `${BASE_URL}/api/dashboard/admtecnico/favorito-observaciones`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            id_usuario: favorito.id_usuario,
            id_concurso: favorito.id_concurso,
            observaciones: observaciones,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Error al guardar observaciones");
      }

      const data = await response.json();
      if (data.success) {
        setObservacionesOriginales(observaciones);
        alert("Observaciones guardadas correctamente");
      } else {
        alert(data.message || "Error al guardar observaciones");
      }
    } catch (error: any) {
      console.error("Error saving observaciones:", error);
      alert("Error al guardar observaciones: " + error.message);
    }
  };

  const handleDueñoAction = async (
    action: "becomeOwner" | "dropOwner" | "assignTo",
    newDueno?: string
  ) => {
    if (!favorito?.id_soporte) return;
    try {
      const resp = await fetch(
        `${BASE_URL}/api/soportes/${favorito.id_soporte}/dueno`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({ action, ...(newDueno && { newDueno }) }),
        }
      );
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Error al actualizar dueño: ${txt}`);
      }
      const data = await resp.json();
      if (data.success) {
        await loadFavorito(favorito.id_concurso, favorito.id_usuario);
        alert(data.message);
      } else {
        alert(data.message || "Error al cambiar dueño");
      }
    } catch (error: any) {
      console.error("Error updating owner:", error);
      alert(`Error al cambiar dueño: ${error.message}`);
    }
  };

  const handleReassign = async () => {
    if (!selectedTecnico) {
      alert("Selecciona un técnico");
      return;
    }
    if (!favorito?.id_soporte) return;
    try {
      const token = getAuthToken();
      if (!token) throw new Error("No token");
      const resp = await fetch(
        `${BASE_URL}/api/soportes/${favorito.id_soporte}/dueno`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            action: "assignTo",
            newDueno: selectedTecnico,
          }),
        }
      );
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Error reasignando: ${txt}`);
      }
      const data = await resp.json();
      if (data.success) {
        await loadFavorito(favorito.id_concurso, favorito.id_usuario);
        alert(data.message);
        setShowReassignModal(false);
      } else {
        alert(data.message || "Error al reasignar");
      }
    } catch (error: any) {
      alert(`Error al reasignar: ${error.message}`);
      console.error(error);
    }
  };
  if (!favorito) {
    return (
      <div className="concurso-container">
        <p>No se encontró el favorito o no hay datos.</p>
      </div>
    );
  }
  const fav = favorito;

  const soyDueño = fav.dueno_id === fav.id_usuario;
  const esAdmin = userRole === "admin";
  const esAdminOTec = userRole === "admin" || userRole === "tecnico";
  const hayDueño = !!fav.dueno_id;

  if (!favorito) return <p>No se encontró el favorito</p>;

  const disableSideButtons =
    mapEstado(favorito.estado_soporte || "En estudio") === "En estudio" ||
    mapEstado(favorito.estado_soporte || "").toLowerCase() === "no viable" ||
    mapEstado(favorito.estado_soporte || "").toLowerCase() === "oculto";

  return (
    <div className="favorito-page">
      <aside className="sticky-sidebar">
        {/* ★ NUEVO: Ver concurso */}
        {favorito.enlace ? (
          <Link
            to={`/concurso/${favorito.id_concurso}`}
            className="sidebar-button"
          >
            Ver concurso
          </Link>
        ) : (
          <button className="sidebar-button" disabled>
            Ver concurso
          </button>
        )}
        {/* ★ Todos los botones ahora llaman a handleButtonClick */}
        <button
          className="sidebar-button"
          onClick={() => handleButtonClick("baja")}
          disabled={disableSideButtons}
        >
          Baja estadística
        </button>

        <button
          className="sidebar-button"
          onClick={() => handleButtonClick("tecnico")}
          disabled={disableSideButtons}
        >
          Soporte técnico
        </button>

        <button
          className="sidebar-button"
          onClick={() => handleButtonClick("administrativo")}
          disabled={disableSideButtons}
        >
          Soporte administrativo
        </button>

        <button
          className="sidebar-button"
          onClick={() => handleButtonClick("juridico")}
          disabled={disableSideButtons}
        >
          Soporte jurídico
        </button>

        {/* Información adicional del cliente (solo para admin/tecnico) */}
        {esAdminOTec && (
          <div className="cliente-info">
            <h4>Información del Cliente</h4>
            <p>
              <strong>Nombre y Empresa</strong> {fav.cliente_nombre || "N/A"}
            </p>
            <p>
              <strong>Fecha de constitución:</strong>{" "}
              {fav.fecha_constitucion || "N/A"}
            </p>
            <p>
              <strong>Habilitación profesional:</strong>{" "}
              {fav.habilitacion_profesional || "N/A"}
            </p>
            <p>
              <strong>Clasificaciones reconocidas:</strong>{" "}
              {fav.clasificaciones_reconocidas || "N/A"}
            </p>
            <p>
              <strong>Cifra anual de negocio:</strong>{" "}
              {fav.cifra_anual_negocio || "N/A"}
            </p>
            <p>
              <strong>Certificados de buena ejecución:</strong>{" "}
              {fav.certificados_cbe || "N/A"}
            </p>
            <p>
              <strong>Plan de igualdad:</strong> {fav.plan_igualdad || "N/A"}
            </p>
            <p>
              <strong>Número de empleados:</strong>{" "}
              {fav.numero_empleados || "N/A"}
            </p>
            <p>
              <strong>Otros contactos:</strong> {fav.otros_contactos || "N/A"}
            </p>
          </div>
        )}
      </aside>

      <div className="concurso-container">
        <div className="concurso-header">
          <div>
            <h1>Favorito</h1>
            <p>Expediente: {fav.codigo_expediente}</p>
            {esAdminOTec &&
              (hayDueño ? (
                <p>
                  Dueño actual: {fav.dueno_nombre} ({fav.dueno_email})
                </p>
              ) : (
                <p>No hay dueño asignado</p>
              ))}
          </div>
          {esAdminOTec && (
            <div className="header-buttons">
              {!hayDueño && (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleDueñoAction("becomeOwner")}
                >
                  Asignarme
                </button>
              )}
              {hayDueño && (soyDueño || esAdmin) && (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleDueñoAction("dropOwner")}
                >
                  Liberar
                </button>
              )}
              {esAdmin && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowReassignModal(true)}
                >
                  Reasignar
                </button>
              )}
            </div>
          )}
        </div>

        {showReassignModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Reasignar soporte a otro técnico</h3>
              <select
                value={selectedTecnico}
                onChange={(e) => setSelectedTecnico(e.target.value)}
              >
                <option value="">Seleccionar técnico...</option>
                {tecnicosActivos.map((t) => (
                  <option key={t.id_usuario} value={t.id_usuario}>
                    {t.nombre} ({t.email})
                  </option>
                ))}
              </select>
              <div className="modal-buttons">
                <button onClick={handleReassign}>Confirmar</button>
                <button onClick={() => setShowReassignModal(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="section">
          <h2>Detalles del Favorito</h2>
          <div className="favorito-details-grid">
            {/* Columna izquierda - Info básica */}
            <div className="favorito-info-column">
              <div className="info-grid">
                <div className="field">
                  <label>Título</label>
                  <span>{fav.titulo}</span>
                </div>
                <div className="field">
                  <label>Enlaces</label>
                  <div className="enlaces-container">
                    {fav.enlace ? (
                      <>
                        <a
                          href={fav.enlace}
                          target="_blank"
                          rel="noreferrer"
                          className="enlace-oficial"
                        >
                          Enlace oficial
                        </a>
                        <Link
                          to={`/concurso/${fav.id_concurso}`}
                          className="enlace-interno"
                        >
                          Ver concurso interno
                        </Link>
                      </>
                    ) : (
                      <span className="no-enlace">No disponible</span>
                    )}
                  </div>
                </div>
              </div>

              {esAdminOTec ? (
                <div className="estado-section">
                  <label htmlFor="estado-select">
                    <strong>Estado Soporte:</strong>
                  </label>
                  <select
                    id="estado-select"
                    value={fav.estado_soporte || "En estudio"}
                    onChange={(e) => handleEstadoChange(e.target.value)}
                  >
                    <option value="En estudio">En estudio</option>
                    <option value="valido">Viable</option>
                    <option value="rechazado">No viable</option>
                    <option value="oculto">Oculto</option>
                    <option value="norecomendado">No recomendado</option>
                  </select>

                  <div className="checkbox-filtro">
                    <label
                      htmlFor="presentado-checkbox"
                      style={{ marginLeft: "1rem" }}
                    >
                      <input
                        type="checkbox"
                        id="presentado-checkbox"
                        checked={!!fav.presentado && fav.presentado === 1}
                        onChange={(e) =>
                          handlePresentadoChange(e.target.checked)
                        }
                      />
                      Presentado
                    </label>
                  </div>
                </div>
              ) : (
                <div className="estado-section">
                  <strong>Estado Soporte:</strong>{" "}
                  <span>{mapEstado(fav.estado_soporte)}</span>
                  <br />
                  <strong>Presentado:</strong>{" "}
                  <span>
                    {fav.presentado && fav.presentado === 1 ? "Sí" : "No"}
                  </span>
                </div>
              )}
            </div>

            {/* Columna derecha - Observaciones (solo admin/tecnico) */}
            {esAdminOTec && (
              <div className="favorito-observaciones-column">
                <div className="observaciones-container">
                  <label htmlFor="observaciones-textarea">
                    <strong>Observaciones:</strong>
                  </label>
                  <textarea
                    id="observaciones-textarea"
                    className="observaciones-textarea"
                    placeholder="Agregar observaciones internas..."
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    rows={6}
                    maxLength={2000}
                  />
                  <div className="observaciones-footer">
                    <span className="observaciones-counter">
                      {observaciones.length}/2000
                    </span>
                    <button
                      className="btn-guardar-observaciones"
                      onClick={saveObservaciones}
                      disabled={observaciones === observacionesOriginales}
                    >
                      Guardar Observaciones
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="section">
          <h2>Chat y Archivos</h2>
          <ChatComponent
            idSoporte={favorito.id_soporte}
            clienteNombre={favorito.cliente_nombre}
            clienteEmail={favorito.cliente_email}
            clienteProfilePic={favorito.cliente_profilepic}
            sendText={true}
            allowFileUpload={true}
            showTypingIndicator={true}
          />
        </div>
      </div>
      {/* ★ NUEVO: modal de confirmación */}
      {confirmModalVisible && confirmType && (
        <div className="modal-overlay-soportes">
          <div className="modal-content-soportes">
            <h3>
              ¿Quieres solicitar el{" "}
              <strong>{tipoConfig[confirmType].label}</strong>?
            </h3>
            <textarea
              value={confirmMessage}
              onChange={(e) => setConfirmMessage(e.target.value)}
              placeholder="Describe tu solicitud…"
              rows={4}
              style={{ width: "100%", margin: "1rem 0" }}
            />
            <div className="modal-buttons-soportes">
              <button
                onClick={handleConfirmYes}
                disabled={!confirmMessage.trim()}
              >
                Sí
              </button>
              <button onClick={handleConfirmNo}>No</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Favorito;
